import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Skeleton } from "../../components/ui/Skeleton.js";

interface Props {
  clubBookId: string;
  myProgress?: number | null;
  totalPages?: number | null;
}

function formatDate(d: Date | string) {
  return new Date(d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Reply thread ────────────────────────────────────────────────────

function ReplyItem({
  reply,
  commentId,
}: {
  reply: { id: string; body: string; createdAt: Date | string; editedAt: Date | string | null; displayName: string; isOwn: boolean };
  commentId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(reply.body);
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const edit = trpc.replies.edit.useMutation({
    onSuccess: () => { utils.replies.list.invalidate({ commentId }); setEditing(false); },
  });
  const del = trpc.replies.delete.useMutation({
    onSuccess: () => utils.replies.list.invalidate({ commentId }),
  });

  return (
    <div className="flex gap-2">
      <div className="w-px flex-shrink-0 mt-1 rounded-full" style={{ background: "var(--color-border)", minHeight: "1rem" }} />
      <div className="flex-1 min-w-0 pb-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
          <span className="text-xs font-medium" style={{ color: "var(--color-foreground)" }}>{reply.displayName}</span>
          <span className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>{formatDate(reply.createdAt)}</span>
          {reply.editedAt && <span className="text-xs italic" style={{ color: "var(--color-muted-foreground)" }}>{t("comments.edited")}</span>}
        </div>
        {editing ? (
          <form onSubmit={(e) => { e.preventDefault(); edit.mutate({ replyId: reply.id, body: draft }); }}>
            <textarea
              className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none transition-all"
              style={{ border: "1px solid var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)", resize: "vertical" }}
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
              onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
            />
            <div className="flex gap-1 mt-1">
              <Button type="submit" size="sm" loading={edit.isPending}>{t("comments.save")}</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setEditing(false); setDraft(reply.body); }}>{t("comments.cancel")}</Button>
            </div>
          </form>
        ) : (
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-foreground)" }}>{reply.body}</p>
        )}
        {reply.isOwn && !editing && (
          <div className="flex gap-1 mt-1">
            <button className="text-xs transition-colors" style={{ color: "var(--color-muted-foreground)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-foreground)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
              onClick={() => setEditing(true)}>{t("common.edit")}</button>
            <button className="text-xs transition-colors" style={{ color: "var(--color-muted-foreground)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-destructive)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
              onClick={() => { if (confirm(t("comments.deleteReply"))) del.mutate({ replyId: reply.id }); }}>{t("common.delete")}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ReplyComposer({ commentId, onDone }: { commentId: string; onDone: () => void }) {
  const [body, setBody] = useState("");
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const post = trpc.replies.post.useMutation({
    onSuccess: () => { utils.replies.list.invalidate({ commentId }); setBody(""); onDone(); },
  });

  return (
    <form
      className="flex gap-2 mt-2 pl-3"
      style={{ borderLeft: "2px solid var(--color-border)" }}
      onSubmit={(e) => { e.preventDefault(); if (body.trim()) post.mutate({ commentId, body: body.trim() }); }}
    >
      <textarea
        className="flex-1 rounded-lg px-3 py-2 text-sm focus:outline-none transition-all"
        style={{ border: "1px solid var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)", resize: "none" }}
        rows={2}
        placeholder={t("comments.placeholder")}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
        onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
        required
      />
      <div className="flex flex-col gap-1">
        <Button type="submit" size="sm" loading={post.isPending}>{t("comments.reply")}</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>{t("comments.cancel")}</Button>
      </div>
    </form>
  );
}

function ReplyThread({ commentId }: { commentId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [composing, setComposing] = useState(false);
  const { t } = useTranslation();

  const { data: replies, isLoading } = trpc.replies.list.useQuery(
    { commentId },
    { enabled: expanded }
  );

  return (
    <div className="mt-2">
      <button
        className="text-xs transition-colors"
        style={{ color: "var(--color-muted-foreground)" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
        onClick={() => { setExpanded((v) => !v); if (!expanded) setComposing(false); }}
      >
        {expanded ? t("comments.hideReplies") : t("comments.reply")}
      </button>

      {expanded && (
        <div className="mt-2 space-y-1">
          {isLoading && <p className="text-xs pl-3" style={{ color: "var(--color-muted-foreground)" }}>{t("common.loading")}</p>}
          {replies?.map((r) => (
            <ReplyItem key={r.id} reply={r} commentId={commentId} />
          ))}
          {!composing ? (
            <button
              className="text-xs pl-3 mt-1 transition-colors"
              style={{ color: "var(--color-muted-foreground)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
              onClick={() => setComposing(true)}
            >
              {t("comments.writeReply")}
            </button>
          ) : (
            <ReplyComposer commentId={commentId} onDone={() => setComposing(false)} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Comment item ────────────────────────────────────────────────────

function CommentItem({
  comment,
  clubBookId,
}: {
  comment: {
    id: string;
    page: number;
    body: string;
    createdAt: Date | string;
    editedAt: Date | string | null;
    displayName: string;
    isOwn: boolean;
  };
  clubBookId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const edit = trpc.comments.edit.useMutation({
    onSuccess: () => {
      utils.comments.list.invalidate({ clubBookId });
      setEditing(false);
    },
  });
  const del = trpc.comments.delete.useMutation({
    onSuccess: () => utils.comments.list.invalidate({ clubBookId }),
  });

  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: "var(--color-card)",
        border: "1px solid var(--color-border)",
        boxShadow: "var(--shadow-xs)",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-2">
            <span
              className="text-xs font-medium px-1.5 py-0.5 rounded"
              style={{
                background: "var(--color-accent-subtle)",
                color: "var(--color-accent-subtle-foreground)",
              }}
            >
              p.{comment.page}
            </span>
            <span className="text-xs font-medium" style={{ color: "var(--color-foreground)" }}>
              {comment.displayName}
            </span>
            <span className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
              {formatDate(comment.createdAt)}
            </span>
            {comment.editedAt && (
              <span className="text-xs italic" style={{ color: "var(--color-muted-foreground)" }}>
                {t("comments.edited")}
              </span>
            )}
          </div>

          {editing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                edit.mutate({ commentId: comment.id, body: draft });
              }}
            >
              <textarea
                className="w-full rounded-lg px-3 py-2 text-sm transition-all focus:outline-none"
                style={{
                  border: "1px solid var(--color-border)",
                  background: "var(--color-background)",
                  color: "var(--color-foreground)",
                  resize: "vertical",
                }}
                rows={3}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
              />
              <div className="flex gap-2 mt-2">
                <Button type="submit" size="sm" loading={edit.isPending}>{t("comments.save")}</Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setEditing(false); setDraft(comment.body); }}
                >
                  {t("comments.cancel")}
                </Button>
              </div>
            </form>
          ) : (
            <p
              className="text-sm leading-relaxed whitespace-pre-wrap"
              style={{ color: "var(--color-foreground)" }}
            >
              {comment.body}
            </p>
          )}

          {!editing && <ReplyThread commentId={comment.id} />}
        </div>

        {comment.isOwn && !editing && (
          <div className="flex gap-1 flex-shrink-0 mt-0.5">
            <button
              className="text-xs px-2 py-0.5 rounded transition-colors"
              style={{ color: "var(--color-muted-foreground)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-foreground)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
              onClick={() => setEditing(true)}
            >
              {t("common.edit")}
            </button>
            <button
              className="text-xs px-2 py-0.5 rounded transition-colors"
              style={{ color: "var(--color-muted-foreground)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-destructive)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
              onClick={() => {
                if (confirm(t("comments.deleteComment"))) del.mutate({ commentId: comment.id });
              }}
            >
              {t("common.delete")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Comment composer ────────────────────────────────────────────────

function CommentComposer({ clubBookId, myProgress, totalPages }: { clubBookId: string; myProgress?: number | null; totalPages?: number | null }) {
  const [page, setPage] = useState("");
  const [body, setBody] = useState("");
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const post = trpc.comments.post.useMutation({
    onSuccess: () => {
      utils.comments.list.invalidate({ clubBookId });
      setBody("");
      setPage("");
    },
  });

  return (
    <form
      className="rounded-xl p-4 space-y-3"
      style={{
        background: "var(--color-card)",
        border: "1px solid var(--color-border)",
        boxShadow: "var(--shadow-sm)",
      }}
      onSubmit={(e) => {
        e.preventDefault();
        const p = parseInt(page, 10);
        if (!isNaN(p) && body.trim()) post.mutate({ clubBookId, page: p, body: body.trim() });
      }}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <label
          className="text-xs font-medium uppercase tracking-widest flex-shrink-0"
          style={{ color: "var(--color-muted-foreground)" }}
        >
          {t("comments.pageLabel")}
        </label>
        <input
          type="number"
          min={0}
          className="w-20 rounded-md px-2 py-1 text-sm focus:outline-none transition-all"
          style={{
            border: "1px solid var(--color-border)",
            background: "var(--color-background)",
            color: "var(--color-foreground)",
          }}
          placeholder={myProgress != null ? String(myProgress) : "0"}
          value={page}
          onChange={(e) => setPage(e.target.value)}
          onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
          required
        />
        <span className="text-xs flex-shrink-0" style={{ color: "var(--color-muted-foreground)" }}>
          {totalPages != null ? t("comments.ofPages", { total: totalPages }) : t("comments.noPageCount")}
        </span>
      </div>
      <textarea
        className="w-full rounded-lg px-3 py-2.5 text-sm transition-all focus:outline-none"
        style={{
          border: "1px solid var(--color-border)",
          background: "var(--color-background)",
          color: "var(--color-foreground)",
          resize: "vertical",
        }}
        rows={3}
        placeholder={t("comments.placeholder")}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
        onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
        required
      />
      {post.error && (
        <p className="text-xs" style={{ color: "var(--color-destructive)" }}>{post.error.message}</p>
      )}
      <Button type="submit" size="sm" loading={post.isPending}>{t("comments.post")}</Button>
    </form>
  );
}

// ── CommentList ─────────────────────────────────────────────────────

export function CommentList({ clubBookId, myProgress, totalPages }: Props) {
  const [offset, setOffset] = useState(0);
  const { t } = useTranslation();
  const { data, isLoading } = trpc.comments.list.useQuery({ clubBookId, offset });

  return (
    <div className="space-y-3">
      <CommentComposer clubBookId={clubBookId} myProgress={myProgress} totalPages={totalPages} />

      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl p-4 space-y-2"
              style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}
            >
              <div className="flex items-center gap-2">
                <Skeleton style={{ width: "2.5rem", height: "0.75rem", borderRadius: "9999px" }} />
                <Skeleton style={{ width: "5rem", height: "0.75rem" }} />
                <Skeleton style={{ width: "4rem", height: "0.75rem" }} />
              </div>
              <Skeleton style={{ height: "2.5rem" }} />
            </div>
          ))}
        </div>
      )}

      {data?.total === 0 && (
        <div
          className="rounded-xl px-5 py-8 text-center"
          style={{
            background: "var(--color-card)",
            border: "1px dashed var(--color-border-strong)",
          }}
        >
          <p className="font-serif italic text-sm" style={{ color: "var(--color-muted-foreground)" }}>
            {t("comments.empty")}
          </p>
        </div>
      )}

      {data?.comments.map((c) => (
        <CommentItem key={c.id} comment={c} clubBookId={clubBookId} />
      ))}

      {data?.hasMore && (
        <Button variant="ghost" size="sm" onClick={() => setOffset((o) => o + 50)}>
          {t("comments.loadMore")}
        </Button>
      )}
    </div>
  );
}
